import { useCallback, useEffect, useState } from 'react';
import { BeakerIcon, ArrowPathIcon, PlusIcon, TrashIcon, PencilIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type MutationLocus } from '../../api/genetics';
import { getApiHostUrl } from '../../config/api';
import { notifyGeneticsCatalogChanged } from '../../utils/geneticsCatalogEvents';
import AddMutationLocusForm from './AddMutationLocusForm';
import AddMutationAlleleForm from './AddMutationAlleleForm';
import EditMutationAlleleForm from './EditMutationAlleleForm';
import EditMutationLocusForm from './EditMutationLocusForm';

export interface SpeciesCatalogSeedInfo {
  hasReferenceSeed: boolean;
  referenceLocusCount: number;
  referenceLabel?: string;
  lociCount: number;
  mutationNames: string[];
  wildTypePhenotype?: string;
  wildTypeNotes?: string;
}

type CatalogSuggestion = {
  locusSymbol: string;
  locusName: string;
  chromosome: string;
  inheritanceMode: string;
  category?: string;
  description?: string;
  rationale: string;
  evidenceLevel?: 'high' | 'medium' | 'low';
  alleles: Array<{ symbol: string; name: string; isWildType?: boolean }>;
};

interface SpeciesMutationCatalogPanelProps {
  speciesId: string;
  /** Called after catalog seed/sync/CRUD so parent can refresh species.mutations */
  onMutationsSynced?: (mutationNames: string[]) => void;
  compact?: boolean;
}

function illustrationSrc(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  const host = getApiHostUrl().replace(/\/$/, '');
  return `${host}${url.startsWith('/') ? url : `/${url}`}`;
}

export default function SpeciesMutationCatalogPanel({
  speciesId,
  onMutationsSynced,
  compact = false,
}: SpeciesMutationCatalogPanelProps) {
  const [loci, setLoci] = useState<MutationLocus[]>([]);
  const [seedInfo, setSeedInfo] = useState<SpeciesCatalogSeedInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingAlleleLocusId, setAddingAlleleLocusId] = useState<string | null>(null);
  const [editingAlleleId, setEditingAlleleId] = useState<string | null>(null);
  const [editingLocusId, setEditingLocusId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [applyingWildType, setApplyingWildType] = useState(false);
  const [suggestions, setSuggestions] = useState<CatalogSuggestion[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState<CatalogSuggestion | null>(null);
  const [feedbackSuggestionSymbol, setFeedbackSuggestionSymbol] = useState<string | null>(
    null,
  );
  const [verificationNote, setVerificationNote] = useState<string | null>(null);
  const [noKnownMutations, setNoKnownMutations] = useState(false);
  const [wildTypeSuggestion, setWildTypeSuggestion] = useState<{
    phenotype: string;
    genotypeNote: string;
    confidence: string;
  } | null>(null);
  const [suggestionDraft, setSuggestionDraft] = useState<{
    locusSymbol: string;
    locusName: string;
    chromosome: string;
    inheritanceMode: string;
    description?: string;
    mutantSymbol: string;
    mutantName: string;
  } | null>(null);

  const notifyCatalogChange = useCallback(
    (mutationNames: string[]) => {
      onMutationsSynced?.(mutationNames);
      notifyGeneticsCatalogChanged(speciesId);
    },
    [onMutationsSynced, speciesId],
  );

  const loadCatalog = useCallback(async () => {
    if (!speciesId) return;
    setLoading(true);
    setError(null);
    try {
      const [info, catalog] = await Promise.all([
        geneticsApi.getSpeciesSeedInfo(speciesId),
        geneticsApi.getSpeciesLoci(speciesId),
      ]);
      setSeedInfo(info);
      setLoci(catalog);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load mutation catalog');
    } finally {
      setLoading(false);
    }
  }, [speciesId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const handleSeed = async () => {
    setSeeding(true);
    setError(null);
    try {
      const result = await geneticsApi.seedSpecies(speciesId);
      if (result.mutationNames?.length) {
        notifyCatalogChange(result.mutationNames);
      }
      await loadCatalog();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to seed mutation catalog');
    } finally {
      setSeeding(false);
    }
  };

  const handleImportLegacy = async () => {
    setImporting(true);
    setError(null);
    try {
      await geneticsApi.importLegacyForSpecies(speciesId);
      await loadCatalog();
      notifyGeneticsCatalogChanged(speciesId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to import legacy genotypes');
    } finally {
      setImporting(false);
    }
  };

  const handleSuggestCatalog = async () => {
    setSuggesting(true);
    setError(null);
    setInfoMessage(null);
    try {
      const result = await geneticsApi.suggestCatalogLoci(speciesId);
      setSuggestions(result.suggestions);
      setActiveSuggestion(null);
      setSuggestionDraft(null);
      setVerificationNote(result.verificationNote);
      setNoKnownMutations(Boolean(result.noKnownMutations));
      setWildTypeSuggestion(result.wildTypeBaseline);
      if (result.message) {
        setInfoMessage(result.message);
      }
    } catch (err: unknown) {
      setSuggestions([]);
      setVerificationNote(null);
      setNoKnownMutations(false);
      setWildTypeSuggestion(null);
      setError(err instanceof Error ? err.message : 'Failed to fetch AI suggestions');
    } finally {
      setSuggesting(false);
    }
  };

  const handleApplyWildType = async () => {
    const phenotype =
      wildTypeSuggestion?.phenotype?.trim() ||
      seedInfo?.wildTypePhenotype?.trim() ||
      '';
    if (!phenotype) {
      setError('No wild-type phenotype available to apply.');
      return;
    }
    setApplyingWildType(true);
    setError(null);
    try {
      const result = await geneticsApi.applyWildTypeBaseline(speciesId, {
        phenotype,
        notes: wildTypeSuggestion?.genotypeNote,
        syncColorDescription: true,
      });
      notifyCatalogChange(result.mutationNames);
      setInfoMessage('Wild-type baseline saved for this species.');
      await loadCatalog();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to apply wild-type baseline');
    } finally {
      setApplyingWildType(false);
    }
  };

  const handleApplySuggestion = (suggestion: CatalogSuggestion) => {
    const wildSymbol = `${suggestion.locusSymbol}+`;
    const mutant = suggestion.alleles.find(
      (a) => a.symbol !== wildSymbol && !a.isWildType,
    );
    setSuggestionDraft({
      locusSymbol: suggestion.locusSymbol,
      locusName: suggestion.locusName,
      chromosome: suggestion.chromosome,
      inheritanceMode: suggestion.inheritanceMode,
      description: suggestion.description ?? suggestion.rationale,
      mutantSymbol: mutant?.symbol ?? '',
      mutantName: mutant?.name ?? '',
    });
    setActiveSuggestion(suggestion);
    setShowAddForm(true);
  };

  const handleRejectSuggestion = async (suggestion: CatalogSuggestion) => {
    setFeedbackSuggestionSymbol(suggestion.locusSymbol);
    setError(null);
    try {
      const result = await geneticsApi.recordCatalogFeedback(speciesId, {
        outcome: 'rejected',
        suggestion,
      });
      if (!result.recorded) throw new Error('Feedback store is unavailable');
      setSuggestions((prev) =>
        prev.filter((item) => item.locusSymbol !== suggestion.locusSymbol),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save suggestion feedback');
    } finally {
      setFeedbackSuggestionSymbol(null);
    }
  };

  const handleSuggestionCreated = async (correction: object) => {
    const reviewedSuggestion = activeSuggestion;
    setShowAddForm(false);
    setSuggestionDraft(null);
    setActiveSuggestion(null);

    if (reviewedSuggestion) {
      try {
        const result = await geneticsApi.recordCatalogFeedback(speciesId, {
          outcome: 'accepted',
          suggestion: reviewedSuggestion,
          correction,
        });
        if (!result.recorded) throw new Error('Feedback store is unavailable');
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? `Locus saved, but feedback was not recorded: ${err.message}`
            : 'Locus saved, but feedback was not recorded',
        );
      }
      setSuggestions((prev) =>
        prev.filter((item) => item.locusSymbol !== reviewedSuggestion.locusSymbol),
      );
    }

    await afterCatalogMutation();
  };

  const handleDeleteAllele = async (alleleId: string, isWildType: boolean) => {
    if (isWildType) {
      setError('Wild-type alleles cannot be deleted.');
      return;
    }
    if (!window.confirm('Delete this allele?')) return;
    setError(null);
    try {
      await geneticsApi.deleteAllele(alleleId);
      await loadCatalog();
      notifyGeneticsCatalogChanged(speciesId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete allele');
    }
  };

  const handleDeleteLocus = async (locusId: string) => {
    if (!window.confirm('Delete this locus and all its alleles? Bird genotypes at this locus will also be removed.')) {
      return;
    }
    setError(null);
    try {
      await geneticsApi.deleteLocus(locusId);
      await loadCatalog();
      const info = await geneticsApi.getSpeciesSeedInfo(speciesId);
      notifyCatalogChange(info.mutationNames ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete locus');
    }
  };

  const afterCatalogMutation = async () => {
    await loadCatalog();
    const info = await geneticsApi.getSpeciesSeedInfo(speciesId);
    notifyCatalogChange(info.mutationNames ?? []);
  };

  if (loading) {
    return <p className="text-sm text-slate-500">Loading mutation catalog…</p>;
  }

  const showSeedButton = seedInfo?.hasReferenceSeed && loci.length === 0;
  const showSyncReferenceButton =
    seedInfo?.hasReferenceSeed &&
    loci.length > 0 &&
    (seedInfo.referenceLocusCount ?? 0) > loci.length;

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <BeakerIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Mutation Catalog</h4>
            <p className="text-xs text-slate-500">
              Species-specific loci for bird genotypes and breeding predictions. Changes sync to birds and pairs.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadCatalog}
          disabled={loading || seeding}
          className="text-xs text-indigo-700 hover:text-indigo-900 flex items-center gap-1 disabled:opacity-50"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)} className="mt-1 text-xs text-indigo-700 underline">
            Dismiss
          </button>
        </div>
      ) : null}

      {infoMessage ? (
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {infoMessage}
        </div>
      ) : null}

      {(seedInfo?.wildTypePhenotype || wildTypeSuggestion) && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-emerald-900">Wild-type baseline (this species)</p>
              <p className="text-sm text-slate-800 mt-1">
                {seedInfo?.wildTypePhenotype || wildTypeSuggestion?.phenotype}
              </p>
              {(seedInfo?.wildTypeNotes || wildTypeSuggestion?.genotypeNote) && (
                <p className="text-xs text-slate-600 mt-1">
                  {seedInfo?.wildTypeNotes || wildTypeSuggestion?.genotypeNote}
                </p>
              )}
            </div>
            {wildTypeSuggestion && (
              <button
                type="button"
                onClick={() => void handleApplyWildType()}
                disabled={applyingWildType}
                className="text-xs text-emerald-800 underline shrink-0 disabled:opacity-50"
              >
                {applyingWildType ? 'Saving…' : 'Apply / update baseline'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {showSeedButton && (
          <button
            type="button"
            onClick={handleSeed}
            disabled={seeding}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {seeding ? 'Seeding…' : 'Load reference catalog'}
          </button>
        )}
        {showSyncReferenceButton && (
          <button
            type="button"
            onClick={handleSeed}
            disabled={seeding}
            className="inline-flex items-center rounded-md border border-indigo-300 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
          >
            {seeding ? 'Syncing…' : 'Sync missing reference loci'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowAddForm((v) => !v)}
          className="inline-flex items-center gap-1 rounded-md border border-indigo-300 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-50"
        >
          <PlusIcon className="h-4 w-4" />
          {showAddForm ? 'Cancel' : 'Add locus'}
        </button>
        <button
          type="button"
          onClick={handleSuggestCatalog}
          disabled={suggesting}
          className="inline-flex items-center gap-1 rounded-md border border-violet-300 px-3 py-2 text-sm font-medium text-violet-800 hover:bg-violet-50 disabled:opacity-50"
        >
          <SparklesIcon className="h-4 w-4" />
          {suggesting ? 'Suggesting…' : 'AI suggest loci'}
        </button>
        {loci.length > 0 && (
          <button
            type="button"
            onClick={handleImportLegacy}
            disabled={importing}
            className="inline-flex items-center rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {importing ? 'Importing…' : 'Import legacy bird genotypes'}
          </button>
        )}
      </div>

      {showAddForm && (
        <AddMutationLocusForm
          speciesId={speciesId}
          initialDraft={suggestionDraft ?? undefined}
          onCreated={handleSuggestionCreated}
          onCancel={() => {
            setShowAddForm(false);
            setSuggestionDraft(null);
            setActiveSuggestion(null);
          }}
        />
      )}

      {(suggestions.length > 0 || verificationNote) && (
        <div className="rounded-md border border-violet-200 bg-violet-50/60 p-3 space-y-3">
          <p className="text-xs font-medium text-violet-900">
            AI suggestions — species-specific review only (nothing saved until you add a locus)
          </p>
          {verificationNote ? (
            <p className="text-xs text-violet-800 bg-white/70 border border-violet-100 rounded px-2 py-1.5">
              {verificationNote}
            </p>
          ) : null}
          {suggestions.length === 0 ? (
            <p className="text-sm text-slate-700">
              {noKnownMutations
                ? 'No verified color mutations were returned for this species. You can still apply the wild-type baseline above.'
                : 'No addable loci were returned. If this species has known mutations, try again or add them manually / load a reference catalog.'}
            </p>
          ) : (
            suggestions.map((suggestion) => (
              <div
                key={suggestion.locusSymbol}
                className="rounded border border-violet-100 bg-white p-3 text-sm"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <span className="font-medium text-slate-900">{suggestion.locusName}</span>
                    <span className="ml-2 font-mono text-xs text-slate-500">
                      ({suggestion.locusSymbol})
                    </span>
                    {suggestion.evidenceLevel ? (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-violet-700">
                        evidence: {suggestion.evidenceLevel}
                      </span>
                    ) : null}
                    <p className="text-xs text-slate-500 mt-1">{suggestion.rationale}</p>
                    {suggestion.description ? (
                      <p className="text-xs text-slate-700 mt-1">
                        <span className="font-medium">Visual effect:</span> {suggestion.description}
                      </p>
                    ) : null}
                    <p className="text-xs text-slate-400 mt-1">
                      {suggestion.inheritanceMode.replace(/_/g, ' ').toLowerCase()}
                      {suggestion.chromosome === 'Z' ? ' · sex-linked' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleApplySuggestion(suggestion)}
                      className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                    >
                      Review &amp; add
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleRejectSuggestion(suggestion)}
                      disabled={feedbackSuggestionSymbol === suggestion.locusSymbol}
                      className="text-xs text-red-600 hover:text-red-800 underline disabled:opacity-50"
                    >
                      {feedbackSuggestionSymbol === suggestion.locusSymbol
                        ? 'Saving…'
                        : 'Reject'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {suggestion.alleles.map((allele, alleleIdx) => (
                    <span
                      key={`${suggestion.locusSymbol}-${allele.symbol}-${alleleIdx}`}
                      className="inline-flex px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700 font-mono"
                    >
                      {allele.symbol} {allele.name}
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showSeedButton && (
        <p className="text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 rounded px-3 py-2">
          Reference catalog available for {seedInfo.referenceLabel} ({seedInfo.referenceLocusCount} loci).
        </p>
      )}

      {!showSeedButton && seedInfo?.hasReferenceSeed && loci.length > 0 && (
        <p className="text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 rounded px-3 py-2">
          Reference catalog loaded for {seedInfo.referenceLabel}
          {showSyncReferenceButton
            ? ` (${seedInfo.referenceLocusCount} reference loci; ${loci.length} in catalog — sync to add missing loci).`
            : '.'}
        </p>
      )}

      {loci.length === 0 && !seedInfo?.hasReferenceSeed ? (
        <p className="text-sm text-slate-500">
          No mutation catalog is defined for this species yet. Use AI suggest for species-specific verified loci, or add loci manually. Reference catalogs exist for budgerigar, Indian ringneck, cockatiel, green-cheeked conure, peach-faced lovebird, African grey, and Eclectus when taxonomy matches.
        </p>
      ) : null}

      {loci.length > 0 ? (
        <div className="space-y-3">
          {loci.map((locus) => (
            <div key={locus.id} className="rounded-md border border-slate-200 bg-white p-3">
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-2">
                {locus.illustrationUrl ? (
                  <img
                    src={illustrationSrc(locus.illustrationUrl)}
                    alt={`${locus.locusName} illustration`}
                    className="h-16 w-16 rounded object-cover border border-slate-200 flex-shrink-0"
                  />
                ) : (
                  <div className="h-16 w-16 rounded border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-[10px] text-slate-400 flex-shrink-0 text-center px-1">
                    No photo
                  </div>
                )}
                <div className="flex-1 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <span className="font-medium text-slate-900">{locus.locusName}</span>
                    <span className="ml-2 font-mono text-xs text-slate-500">({locus.locusSymbol})</span>
                    {locus.description ? (
                      <p className="text-xs text-slate-600 mt-1">
                        <span className="font-medium text-slate-700">Visual effect:</span>{' '}
                        {locus.description}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1 italic">No visual effect described yet.</p>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 sm:text-right">
                    <div>{locus.inheritanceMode.replace(/_/g, ' ').toLowerCase()}</div>
                    <div>{locus.chromosome === 'Z' ? 'Sex-linked (Z)' : 'Autosomal'}</div>
                    {locus.category ? <div className="capitalize">{locus.category}</div> : null}
                    {locus.linkageGroup ? (
                      <div className="text-indigo-600">Linkage: {locus.linkageGroup}</div>
                    ) : null}
                    {locus.linkageRecombinationRate != null && locus.linkageRecombinationRate > 0 ? (
                      <div className="text-indigo-600">
                        Recombination: {Math.round(locus.linkageRecombinationRate * 100)}%
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingLocusId(locus.id);
                        setEditingAlleleId(null);
                        setAddingAlleleLocusId(null);
                      }}
                      className="mt-1 inline-flex items-center gap-0.5 text-indigo-700 hover:text-indigo-900"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteLocus(locus.id)}
                      className="mt-1 inline-flex items-center gap-0.5 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
              {editingLocusId === locus.id ? (
                <EditMutationLocusForm
                  locus={locus}
                  onSaved={async () => {
                    setEditingLocusId(null);
                    await afterCatalogMutation();
                  }}
                  onCancel={() => setEditingLocusId(null)}
                />
              ) : null}
              <div className="flex flex-wrap gap-1.5">
                {locus.alleles.map((allele) => (
                  <div key={allele.id} className="inline-flex flex-col">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        allele.isWildType
                          ? 'bg-slate-100 text-slate-700'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                      title={allele.phenotypeDescription}
                    >
                      <span className="font-mono mr-1">{allele.symbol}</span>
                      {allele.name}
                      {!allele.isWildType && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAlleleId(allele.id);
                              setAddingAlleleLocusId(null);
                            }}
                            className="ml-1.5 text-indigo-700 hover:text-indigo-900"
                            title="Edit allele"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAllele(allele.id, allele.isWildType)}
                            className="ml-1 text-red-600 hover:text-red-800"
                            title="Delete allele"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </span>
                    {editingAlleleId === allele.id ? (
                      <EditMutationAlleleForm
                        allele={allele}
                        onSaved={async () => {
                          setEditingAlleleId(null);
                          await afterCatalogMutation();
                        }}
                        onCancel={() => setEditingAlleleId(null)}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
              {addingAlleleLocusId === locus.id ? (
                <AddMutationAlleleForm
                  locusId={locus.id}
                  locusSymbol={locus.locusSymbol}
                  onCreated={async () => {
                    setAddingAlleleLocusId(null);
                    await afterCatalogMutation();
                  }}
                  onCancel={() => setAddingAlleleLocusId(null)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingAlleleLocusId(locus.id)}
                  className="mt-2 text-xs text-indigo-700 hover:text-indigo-900"
                >
                  + Add allele
                </button>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {seedInfo?.mutationNames?.length ? (
        <div className="text-xs text-slate-600">
          <span className="font-medium">Synced to species:</span>{' '}
          {seedInfo.mutationNames.join(', ')}
        </div>
      ) : null}
    </div>
  );
}
